/**
 * RTK Query の HTTP 通信基盤
 *
 * 【このファイルの役割】
 * すべての API 呼び出しが通る「関所」のようなもの。
 *
 * 1. baseQuery:
 *    - fetchBaseQuery をベースに Cookie 自動送信と CSRF ヘッダ付与を設定
 *    - Axios の代わりにブラウザ標準の fetch API を使う（RTK Query 公式推奨）
 *
 * 2. baseQueryWithReauth:
 *    - baseQuery をラップして「401 エラー → 自動リフレッシュ → リトライ」を実現
 *    - async-mutex で排他制御し、複数 API が同時に 401 を受けても
 *      リフレッシュは 1 回だけに集約する
 *
 * 【credentials: 'include' とは？】
 * ブラウザに「Cookie を自動でリクエストに含めてね」と指示する設定。
 * これにより、HttpOnly Cookie に入ったアクセストークンが
 * 毎回自動でサーバーに送られる。
 * フロント側の JS からはトークンに一切触れない（= XSS で盗めない）。
 */

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { getCsrfTokenFromCookie } from '../utils/csrf';
import { logout } from '../store/slices/authSlice';

/**
 * Mutex（排他制御）のインスタンス。
 * アプリ全体で 1 つだけ存在し、リフレッシュの多重発火を防ぐ。
 */
const mutex = new Mutex();

/**
 * 基本の HTTP クライアント設定。
 *
 * - baseUrl: '/api' → Vite のプロキシ経由でバックエンドへ転送
 * - credentials: 'include' → Cookie を自動送受信
 * - prepareHeaders: 毎リクエスト実行され、CSRF トークンをヘッダに付与
 */
const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  credentials: 'include',
  prepareHeaders: (headers) => {
    // CSRF トークンを Cookie から読み取ってヘッダに付与
    // （POST/PUT/DELETE で必要。GET は CSRF 対策不要）
    const csrfToken = getCsrfTokenFromCookie();
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
    // XMLHttpRequest であることを示すヘッダ（CSRF 対策の補助）
    headers.set('X-Requested-With', 'XMLHttpRequest');
    // テナントID（開発用固定値: tA8gH2xS = テスト株式会社）
    headers.set('X-Tenant-Id', 'tA8gH2xS');
    return headers;
  },
});

/**
 * 自動リフレッシュ付き baseQuery。
 *
 * 【処理フロー】
 * 1. Mutex がロック中なら、ロック解除を待つ（他が既にリフレッシュ中）
 * 2. 元のリクエストを送信
 * 3. 401 が返ってきたら：
 *    a. Mutex ロック取得（最初の 1 人だけ）
 *    b. /auth/refresh を呼んで新トークンを取得（Cookie が自動更新される）
 *    c. 成功 → 元のリクエストをリトライ
 *    d. 失敗 → ログアウト（セッション切れ）
 *    e. Mutex ロック解除
 * 4. 他のリクエストはロック解除後にリトライ → 新 Cookie で成功
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // 他のリクエストがリフレッシュ中なら完了を待つ
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      // 自分が最初にリフレッシュを行う
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          { url: '/auth/refresh', method: 'POST' },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          // リフレッシュ成功 → Cookie が更新されているので、元のリクエストをリトライ
          result = await baseQuery(args, api, extraOptions);
        } else {
          // リフレッシュ失敗 → セッション切れなので即座にログアウト
          // ※ invalidateTags(['Auth']) だと useGetMeQuery が再 fetch → 401 →
          //   再 refresh → 失敗 → invalidateTags → … の無限ループになるリスクがある。
          //   logout() で isLoggedOut=true にすれば useGetMeQuery の skip が効いて停止する。
          api.dispatch(logout());
        }
      } finally {
        // 成功でも失敗でも必ずロックを解除する
        release();
      }
    } else {
      // 別のリクエストがリフレッシュ中 → 完了を待ってからリトライ
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
